import React, { useEffect, useMemo, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../redux/actions';

import AddIcon from '@mui/icons-material/Add';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

let keySeed = 0;
const nextKey = () => `t${++keySeed}`;

const move = (list, index, delta) => {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const copy = [...list];
  [copy[index], copy[target]] = [copy[target], copy[index]];
  return copy;
};

const limitText = (value) => (value === null || value === undefined ? '' : String(value));
const limitValue = (text) => {
  const trimmed = `${text}`.trim();
  if (trimmed === '') return null;
  const number = Number(trimmed);
  return Number.isFinite(number) ? number : null;
};

// Editor rows carry the catalog limits so they can be shown and edited.
// Only limits the user changes are sent, since they are account-wide.
function toDraft(template, catalogById) {
  return {
    id: template?.id || 'new',
    name: template?.name || '',
    components: (template?.components || []).map((c) => {
      const catalogTests = new Map((catalogById[c.component_id]?.tests || []).map((t) => [t.test_field_id, t]));
      return {
        key: nextKey(),
        component_id: c.component_id,
        hidden: !!c.hidden,
        open: false,
        tests: c.tests.map((t) => ({
          key: nextKey(),
          test_field_id: t.test_field_id,
          min: limitText(catalogTests.get(t.test_field_id)?.min_limit),
          max: limitText(catalogTests.get(t.test_field_id)?.max_limit),
          limitsChanged: false,
        })),
      };
    }),
  };
}

function toPayload(draft) {
  return draft.components.map((c) => ({
    ...(c.component_id ? { component_id: c.component_id } : { new_component: c.new_component }),
    hidden: c.hidden,
    tests: c.tests.map((t) => (t.limitsChanged
      ? { test_field_id: t.test_field_id, min_limit: limitValue(t.min), max_limit: limitValue(t.max) }
      : { test_field_id: t.test_field_id })),
  }));
}

const EMPTY_CATALOG = [];

const ReportTemplatesCard = ({
  customerId, authToken, testFields, reportTemplates, templateCatalogs,
  fetchTestFields, fetchReportTemplates, createReportTemplate, updateReportTemplate, deleteReportTemplate, createReport,
}) => {
  const [loaded, setLoaded] = useState(null); // null while loading, false if this user can't manage the account
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [newSystem, setNewSystem] = useState(null);

  // This account's templates and catalog, from the store.
  const templates = useMemo(() => (
    Object.values(reportTemplates || {})
      .filter((t) => `${t.customer_id}` === `${customerId}`)
      .sort((a, b) => a.name.localeCompare(b.name))
  ), [reportTemplates, customerId]);
  const catalog = templateCatalogs?.[customerId] || EMPTY_CATALOG;

  const catalogById = useMemo(() => Object.fromEntries(catalog.map((c) => [c.id, c])), [catalog]);
  const testNames = useMemo(() => {
    const names = {};
    catalog.forEach((c) => c.tests.forEach((t) => { names[t.test_field_id] = t.name; }));
    (testFields || []).forEach((t) => { names[t.id] = t.test_name; });
    return names;
  }, [catalog, testFields]);

  useEffect(() => {
    setLoaded(null);
    setDraft(null);
    setError(null);
    setNotice(null);
    fetchReportTemplates(customerId, authToken)
      .then(() => setLoaded(true))
      .catch(() => setLoaded(false));
  }, [customerId, authToken, fetchReportTemplates]);

  // Tracks which item is busy and surfaces the server's error for a request.
  // Results land in the store through the action itself.
  const run = (id, request, onSuccess) => {
    setBusy(id);
    setError(null);
    setNotice(null);
    return request
      .then((json) => onSuccess?.(json))
      .catch((e) => setError(e.message))
      .finally(() => setBusy(null));
  };

  const openEditor = (template) => {
    if (!testFields?.length) fetchTestFields(authToken, { silent: true });
    setError(null);
    setNotice(null);
    setNewSystem(null);
    if (template) {
      setDraft(toDraft(template, catalogById));
    } else {
      // New templates start from the account's active systems and their tests.
      const components = catalog.filter((c) => c.active).map((c) => ({
        component_id: c.id,
        hidden: false,
        tests: c.tests.map((t) => ({ test_field_id: t.test_field_id })),
      }));
      setDraft(toDraft({ name: '', components }, catalogById));
    }
  };

  const duplicate = (template) => {
    const taken = new Set(templates.map((t) => t.name.toLowerCase()));
    let name = `${template.name} (copy)`;
    for (let n = 2; taken.has(name.toLowerCase()); n += 1) name = `${template.name} (copy ${n})`;
    run(template.id, createReportTemplate({ customer_id: customerId, source_id: template.id, name }, authToken), () => setNotice(`Created ${name}.`));
  };

  // Starts a service report laid out from the template and opens it.
  const newReport = (template) => {
    createReport(customerId, 'sr', null, authToken, (newReportUrl) => {
      window.location.href = `/reports/edit?${newReportUrl}`;
    }, template.id);
  };

  const remove = (template) => {
    if (!window.confirm(`Delete the ${template.name} template? Reports already created from it are not affected.`)) return;
    run(template.id, deleteReportTemplate(template.id, authToken));
  };

  const save = () => {
    const body = { name: draft.name, components: toPayload(draft) };
    if (draft.id === 'new') {
      run('draft', createReportTemplate({ customer_id: customerId, ...body }, authToken), (json) => {
        setDraft(null);
        setNotice(`Created ${json.template.name}.`);
      });
    } else {
      run('draft', updateReportTemplate({ id: draft.id, ...body }, authToken), (json) => {
        setDraft(null);
        setNotice(`Saved ${json.template.name}. New reports from it use this layout; existing reports don't change.`);
      });
    }
  };

  // ---- draft editing helpers ----
  const setComponents = (fn) => setDraft((d) => ({ ...d, components: fn(d.components) }));
  const updateComponent = (key, changes) => setComponents((list) => list.map((c) => (c.key === key ? { ...c, ...changes } : c)));
  const updateTests = (key, fn) => setComponents((list) => list.map((c) => (c.key === key ? { ...c, tests: fn(c.tests) } : c)));

  const addCatalogSystem = (componentId) => {
    const comp = catalogById[componentId];
    if (!comp) return;
    setComponents((list) => [...list, {
      key: nextKey(),
      component_id: comp.id,
      hidden: false,
      open: true,
      tests: comp.tests.map((t) => ({
        key: nextKey(),
        test_field_id: t.test_field_id,
        min: limitText(t.min_limit),
        max: limitText(t.max_limit),
        limitsChanged: false,
      })),
    }]);
  };

  const addNewSystem = () => {
    const name = newSystem.name.trim();
    if (!name) return;
    setComponents((list) => [...list, {
      key: nextKey(),
      component_id: null,
      new_component: { name, group: newSystem.group.trim() },
      hidden: false,
      open: true,
      tests: [],
    }]);
    setNewSystem(null);
  };

  const addTest = (component, testFieldId) => {
    const id = Number(testFieldId);
    if (!id) return;
    const catalogTest = catalogById[component.component_id]?.tests.find((t) => t.test_field_id === id);
    updateTests(component.key, (tests) => [...tests, {
      key: nextKey(),
      test_field_id: id,
      min: limitText(catalogTest?.min_limit),
      max: limitText(catalogTest?.max_limit),
      limitsChanged: false,
    }]);
  };

  // Shown as the Report templates tab of the account page's Reports card. It
  // loads when the tab opens, so assignments made on the Operators card show here.
  if (loaded === null) return <div className="template-empty">Loading templates…</div>;
  // A failed load means this user can't manage this account.
  if (!loaded) return <div className="template-empty">Report templates aren't available for this account.</div>;

  const renderList = () => (
    <>
      {templates.length === 0 ? (
        <div className="template-empty">
          No templates yet. Operators can't start reports until they're assigned one on the Operators card.
        </div>
      ) : (
        <div className="template-list">
          {templates.map((t) => {
            const testCount = t.components.reduce((sum, c) => sum + c.tests.length, 0);
            const retired = t.components.filter((c) => catalogById[c.component_id] && !catalogById[c.component_id].active).length;
            const expanded = draft?.id === t.id;
            // Rows expand in place to edit; clicking an open row closes it without saving.
            const toggle = () => {
              if (busy) return;
              if (expanded) {
                setDraft(null);
                setError(null);
              } else {
                openEditor(t);
              }
            };
            return (
              <div key={t.id} className={`template-list-item${expanded ? ' expanded' : ''}`}>
              <div
                className="template-list-row"
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={toggle}
                onKeyDown={(e) => {
                  if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    toggle();
                  }
                }}
              >
                <div className="template-list-main">
                  <div className="contact-cell-primary">{t.name}</div>
                  <div className="contact-cell-secondary">
                    {t.components.length} systems · {testCount} tests
                    {retired > 0 && ` · ${retired} retired`}
                  </div>
                  <div className="contact-cell-secondary">
                    {t.assigned_to.length
                      ? `Assigned to ${t.assigned_to.map((u) => u.name).join(', ')}`
                      : null}
                  </div>
                </div>
                <div className="template-list-actions" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="contact-edit-btn" disabled={!!busy} onClick={() => newReport(t)}>
                    <AddIcon /><span>New report</span>
                  </button>
                  <button type="button" className="template-icon-btn" disabled={!!busy} title="Duplicate" aria-label={`Duplicate ${t.name}`} onClick={() => duplicate(t)}>
                    <ContentCopyRoundedIcon />
                  </button>
                  <button
                    type="button"
                    className="template-icon-btn is-danger"
                    disabled={!!busy || t.assigned_to.length > 0}
                    title={t.assigned_to.length ? 'Assign these operators another template before deleting' : 'Delete'}
                    aria-label={`Delete ${t.name}`}
                    onClick={() => remove(t)}
                  >
                    <DeleteOutlineRoundedIcon />
                  </button>
                </div>
                <ExpandMoreRoundedIcon className={`template-list-chevron${expanded ? ' expanded' : ''}`} aria-hidden="true" />
              </div>
              {expanded && <div className="template-list-detail">{renderEditor()}</div>}
              </div>
            );
          })}
        </div>
      )}

      {draft?.id === 'new' ? (
        <div className="template-list-item expanded">
          <div className="template-list-detail is-new">{renderEditor()}</div>
        </div>
      ) : (
        <div className="add-new" onClick={() => openEditor(null)}>
          <AddIcon />
          <span>New template</span>
        </div>
      )}
    </>
  );

  const renderEditor = () => {
    const usedIds = new Set(draft.components.map((c) => c.component_id).filter(Boolean));
    const available = catalog.filter((c) => !usedIds.has(c.id));
    const missingActive = available.filter((c) => c.active);
    const sortedTestFields = [...(testFields || [])].sort((a, b) => `${a.test_name}`.localeCompare(`${b.test_name}`));

    return (
      <div className="template-editor">
        <label className="template-field">
          <span>Template name</span>
          <input
            className="contact-input"
            value={draft.name}
            placeholder="e.g. Boiler operator"
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </label>

        {missingActive.length > 0 && (
          <div className="template-hint">
            Not in this template: {missingActive.map((c) => c.name).join(', ')}
          </div>
        )}

        <div className="template-section-head">
          <span>Systems</span>
          <span className="template-section-count">{draft.components.length}</span>
        </div>

        {/* One bordered group: the systems, then the controls that add to them. */}
        <div className="template-group">
        <div className="template-systems">
          {draft.components.map((c, index) => {
            const catalogComp = catalogById[c.component_id];
            const name = catalogComp?.name || c.new_component?.name;
            const group = catalogComp?.group || c.new_component?.group;
            const retired = catalogComp && !catalogComp.active;
            const usedTests = new Set(c.tests.map((t) => t.test_field_id));

            return (
              <div key={c.key} className={`template-system${c.hidden ? ' is-hidden' : ''}${retired ? ' is-retired' : ''}`}>
                <div className="template-system-head">
                  <button
                    type="button"
                    className={`template-icon-btn template-expand${c.open ? ' open' : ''}`}
                    aria-label={c.open ? 'Collapse tests' : 'Show tests'}
                    onClick={() => updateComponent(c.key, { open: !c.open })}
                  >
                    <ExpandMoreRoundedIcon />
                  </button>
                  <div className="template-system-name" onClick={() => updateComponent(c.key, { open: !c.open })}>
                    <span className="template-system-title">{name}</span>
                    <span className="template-system-meta">
                      {group ? `${group} · ` : ''}{c.tests.length} test{c.tests.length === 1 ? '' : 's'}
                      {!c.component_id && ' · new system'}
                    </span>
                  </div>
                  {retired && <span className="contact-primary-badge template-badge-muted">Retired · skipped</span>}
                  {c.hidden && <span className="contact-primary-badge template-badge-muted">Hidden</span>}
                  <div className="template-system-actions">
                    <button type="button" className="template-icon-btn" aria-label="Move up" disabled={index === 0}
                      onClick={() => setComponents((list) => move(list, index, -1))}>
                      <ArrowUpwardRoundedIcon />
                    </button>
                    <button type="button" className="template-icon-btn" aria-label="Move down" disabled={index === draft.components.length - 1}
                      onClick={() => setComponents((list) => move(list, index, 1))}>
                      <ArrowDownwardRoundedIcon />
                    </button>
                    <button type="button" className="template-icon-btn" aria-label={c.hidden ? 'Show on reports' : 'Start hidden'}
                      title={c.hidden ? 'Hidden on new reports' : 'Shown on new reports'}
                      onClick={() => updateComponent(c.key, { hidden: !c.hidden })}>
                      {c.hidden ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                    </button>
                    <button type="button" className="template-icon-btn is-danger" aria-label="Remove from template"
                      onClick={() => setComponents((list) => list.filter((x) => x.key !== c.key))}>
                      <CloseIcon />
                    </button>
                  </div>
                </div>

                {c.open && (
                  <div className="template-tests">
                    {c.tests.length === 0 && <div className="template-tests-empty">No tests yet</div>}
                    {c.tests.length > 0 && (
                      <div className="template-test template-test-head" aria-hidden="true">
                        <span>Test</span>
                        <span>Min</span>
                        <span>Max</span>
                        <span className="template-test-actions" />
                      </div>
                    )}
                    {c.tests.map((t, testIndex) => (
                      <div key={t.key} className="template-test">
                        <span className="template-test-name">{testNames[t.test_field_id] || `Test #${t.test_field_id}`}</span>
                        <input
                          className="contact-input template-limit"
                          inputMode="decimal"
                          placeholder="Min"
                          aria-label="Minimum limit"
                          value={t.min}
                          onChange={(e) => updateTests(c.key, (tests) => tests.map((x) => (x.key === t.key ? { ...x, min: e.target.value, limitsChanged: true } : x)))}
                        />
                        <input
                          className="contact-input template-limit"
                          inputMode="decimal"
                          placeholder="Max"
                          aria-label="Maximum limit"
                          value={t.max}
                          onChange={(e) => updateTests(c.key, (tests) => tests.map((x) => (x.key === t.key ? { ...x, max: e.target.value, limitsChanged: true } : x)))}
                        />
                        <div className="template-test-actions">
                          <button type="button" className="template-icon-btn" aria-label="Move test up" disabled={testIndex === 0}
                            onClick={() => updateTests(c.key, (tests) => move(tests, testIndex, -1))}>
                            <ArrowUpwardRoundedIcon />
                          </button>
                          <button type="button" className="template-icon-btn" aria-label="Move test down" disabled={testIndex === c.tests.length - 1}
                            onClick={() => updateTests(c.key, (tests) => move(tests, testIndex, 1))}>
                            <ArrowDownwardRoundedIcon />
                          </button>
                          <button type="button" className="template-icon-btn is-danger" aria-label="Remove test"
                            onClick={() => updateTests(c.key, (tests) => tests.filter((x) => x.key !== t.key))}>
                            <CloseIcon />
                          </button>
                        </div>
                      </div>
                    ))}
                    <select
                      className="contact-input template-select browser-default"
                      value=""
                      onChange={(e) => addTest(c, e.target.value)}
                    >
                      <option value="">{testFields?.length ? '+ Add test…' : 'Loading tests…'}</option>
                      {sortedTestFields.filter((tf) => !usedTests.has(tf.id)).map((tf) => (
                        <option key={tf.id} value={tf.id}>{tf.test_name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
          {draft.components.length === 0 && <div className="template-empty">No systems in this template yet.</div>}
        </div>

        <div className="template-add-system">
          {available.length > 0 && (
            <select className="contact-input template-select browser-default" value="" onChange={(e) => addCatalogSystem(Number(e.target.value))}>
              <option value="">+ Add an account system…</option>
              {available.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.group ? ` (${c.group})` : ''}{c.active ? '' : ' — retired'}</option>
              ))}
            </select>
          )}
          {newSystem ? (
            <div className="template-new-system">
              <input className="contact-input" placeholder="System name" value={newSystem.name}
                onChange={(e) => setNewSystem({ ...newSystem, name: e.target.value })} />
              <input className="contact-input" placeholder="Group (optional)" value={newSystem.group}
                onChange={(e) => setNewSystem({ ...newSystem, group: e.target.value })} />
              <button type="button" className="contact-edit-btn" disabled={!newSystem.name.trim()} onClick={addNewSystem}>
                <AddIcon /><span>Add</span>
              </button>
              <button type="button" className="contact-edit-btn" onClick={() => setNewSystem(null)}>
                <CloseIcon /><span>Cancel</span>
              </button>
            </div>
          ) : (
            <button type="button" className="contact-edit-btn" onClick={() => setNewSystem({ name: '', group: '' })}>
              <AddIcon /><span>New system</span>
            </button>
          )}
        </div>
        </div>

        <div className="template-editor-actions">
          <span className="template-footnote">
            Limits are shared with every report on this account. New systems are added to the account too.
          </span>
          <button type="button" className="contact-btn" disabled={busy === 'draft'} onClick={() => { setDraft(null); setError(null); }}>
            <CloseIcon /><span>Cancel</span>
          </button>
          <div className="contact-btn-wrap">
            {busy === 'draft' && <div className="saving-flash shown" aria-hidden="true" />}
            <button type="button" className="contact-btn contact-btn-primary template-save" disabled={busy === 'draft' || !draft.name.trim()} onClick={save}>
              <DoneIcon /><span>{busy === 'draft' ? 'Saving...' : 'Save template'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="report-templates-panel">
      {notice && <div className="operator-notice">{notice}</div>}
      {error && <div className="operator-error">{error}</div>}
      {renderList()}
    </div>
  );
};

function mapStateToProps(state) {
  return {
    authToken: state.authToken,
    testFields: state.testFields,
    reportTemplates: state.reportTemplates,
    templateCatalogs: state.templateCatalogs,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ReportTemplatesCard);
